"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMDB = void 0;
const ms_spectrum_1 = require("ms-spectrum");
const appendFragmentsInfo_js_1 = require("./append/appendFragmentsInfo.js");
const fromArray_js_1 = require("./from/fromArray.js");
const fromMolecules_js_1 = require("./from/fromMolecules.js");
const fromNucleicSequence_js_1 = require("./from/fromNucleicSequence.js");
const fromPeptidicSequence_js_1 = require("./from/fromPeptidicSequence.js");
const fromRange_js_1 = require("./from/fromRange.js");
const loadCommercials_js_1 = require("./loadCommercials.js");
const loadGoogleSheet_js_1 = require("./loadGoogleSheet.js");
const loadKnapSack_js_1 = require("./loadKnapSack.js");
const search_js_1 = require("./search.js");
const searchMSEM_js_1 = require("./searchMSEM.js");
const searchSimilarity_js_1 = require("./searchSimilarity.js");
__exportStar(require("./massShifts.js"), exports);
__exportStar(require("./util/fetchJSON.js"), exports);
/**
 * A class that deals with database of monoisotopic mass and molecular formula
 */
class EMDB {
    constructor() {
        this.databases = {};
        this.experimentalSpectrum = undefined;
    }
    /**
     *
     * @param {*} data
     * @param {object} [options={}]
     * @param {boolean} [options.normed=true] Should we normed (sum Y to 1) the experimental spectrum ?
     * @param {number} [options.threshold=0.00025] Threshold used for peak picking
     */
    setExperimentalSpectrum(data, options = {}) {
        const { normed = true, threshold = 0.00025 } = options;
        this.experimentalSpectrum = new ms_spectrum_1.Spectrum(data, { threshold });
        if (normed) {
            this.experimentalSpectrum.normedY();
        }
        return this.experimentalSpectrum;
    }
    /**
     * Add a new database using the KnapSack content
     * @param {*} options
     */
    async loadKnapSack(options = {}) {
        const { databaseName = 'knapSack', forceReload = false } = options;
        if (this.databases[databaseName] && !forceReload)
            return;
        this.databases[databaseName] = await (0, loadKnapSack_js_1.loadKnapSack)();
    }
    /**
     * Add a new database of 12000 commercial products
     * @param {*} options
     */
    async loadCommercials(options = {}) {
        const { databaseName = 'commercials', forceReload = false } = options;
        if (this.databases[databaseName] && !forceReload)
            return;
        this.databases[databaseName] = await (0, loadCommercials_js_1.loadCommercials)();
    }
    get(databaseName) {
        return this.databases[databaseName];
    }
    /**
     * Load the contaminants database from a google sheet document
     * @param {object} [options={}]
     * @param {string} [options.databaseName='contaminants']
     * @param {string} [options.forceReload=false]
     */
    async loadContaminants(options = {}) {
        const { databaseName = 'contaminants', forceReload = false } = options;
        if (this.databases[databaseName] && !forceReload)
            return;
        this.databases[databaseName] = await (0, loadGoogleSheet_js_1.loadGoogleSheet)();
    }
    /**
     * Load a google sheet containing MF information
     * @param {object} [options={}]
     * @param {string} [options.databaseName='sheet']
     * @param {string} [options.forceReload=false]
     */
    async loadGoogleSheet(options = {}) {
        const { databaseName = 'sheet', forceReload = false } = options;
        if (this.databases[databaseName] && !forceReload)
            return;
        this.databases[databaseName] = await (0, loadGoogleSheet_js_1.loadGoogleSheet)();
    }
    async loadTest() {
        await this.fromArray(['C1-100'], {
            databaseName: 'test',
            ionizations: '+',
        });
    }
    async loadNeutralTest(options = {}) {
        const { maxC = 100 } = options;
        await this.fromArray([`C1-${maxC}`], { databaseName: 'test' });
    }
    async fromMonoisotopicMass(mass, options = {}) {
        const { databaseName = 'monoisotopic', append = false } = options;
        let result = await (0, ms_spectrum_1.fromMonoisotopicMass)(mass, options);
        replaceOrAppend(this, databaseName, result.mfs, append);
        return result;
    }
    async fromArray(sequence, options = {}) {
        const { databaseName = 'generated', append = false, estimate } = options;
        const results = await (0, fromArray_js_1.fromArray)(sequence, options);
        if (estimate)
            return results;
        replaceOrAppend(this, databaseName, results, append);
    }
    async fromMolecules(entries, ocl, options = {}) {
        const { databaseName = 'molecules', append = false } = options;
        const results = await (0, fromMolecules_js_1.fromMolecules)(entries, ocl, options);
        replaceOrAppend(this, databaseName, results, append);
    }
    async fromRange(sequence, options = {}) {
        const { databaseName = 'generated', append = false, estimate } = options;
        const results = await (0, fromRange_js_1.fromRange)(sequence, options);
        if (estimate)
            return results;
        replaceOrAppend(this, databaseName, results, append);
    }
    async fromPeptidicSequence(sequence, options = {}) {
        const { databaseName = 'peptidic', append = false, estimate } = options;
        const results = await (0, fromPeptidicSequence_js_1.fromPeptidicSequence)(sequence, options);
        if (estimate)
            return results;
        replaceOrAppend(this, databaseName, results, append);
    }
    /**
     *
     * @param {string} databaseName
     * @param {object} [options={}]
     * @param {number} [options.precision=100]
     * @param {string} [options.ionizations='']
     * @returns
     */
    async appendFragmentsInfo(databaseName, options = {}) {
        const database = this.databases[databaseName];
        await (0, appendFragmentsInfo_js_1.appendFragmentsInfo)(this.experimentalSpectrum, database, options);
        return database;
    }
    async fromNucleicSequence(sequence, options = {}) {
        const { databaseName = 'nucleic', append = false, estimate } = options;
        const results = await (0, fromNucleicSequence_js_1.fromNucleicSequence)(sequence, options);
        if (estimate)
            return results;
        replaceOrAppend(this, databaseName, results, append);
    }
    listDatabases() {
        return Object.keys(this.databases).sort();
    }
    getInfo() {
        return {
            databases: Object.keys(this.databases)
                .sort()
                .map((key) => {
                return { name: key, nbEntries: this.databases[key].length };
            }),
        };
    }
    search(filter, options = {}) {
        return (0, search_js_1.search)(this, filter, options);
    }
    searchMSEM(filter, options = {}) {
        return (0, searchMSEM_js_1.searchMSEM)(this, filter, options);
    }
    searchSimilarity(options = {}) {
        return (0, searchSimilarity_js_1.searchSimilarity)(this, options);
    }
}
exports.EMDB = EMDB;
function replaceOrAppend(emdb, databaseName, results, append = false) {
    if (!emdb.databases[databaseName] || !append) {
        emdb.databases[databaseName] = results;
        return;
    }
    emdb.databases[databaseName] = emdb.databases[databaseName].concat(results);
}
//# sourceMappingURL=index.js.map